import { Component } from '../../components/core/component.js';
import { ComponentList } from '../../components/core/component-list.js';
import { IEntity } from '../../components/core/entity.js';
import { ICopyWithBusinessRef } from '../../contracts/copy-with-business-ref.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { VersionRange } from '../core/version-range.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { BusinessModuleRef } from './business-module-ref.js';

export enum ProfileElementStatus {
  Live = 'LIVE',
  Archived = 'ARCHIVED',
}

export class ProfileElement extends DataModel implements IEntity, ICopyWithBusinessRef {
  static readonly TYPE_ID: string = '0c418ade165b4ac2a8f4ce857795fbfd';
  static readonly SCHEMA_VERSION: number = 3;

  constructor(
    public readonly uniqueId: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly name: string,
    public readonly status: ProfileElementStatus,
    public readonly webRoute: string | null,
    public readonly components: ComponentList,
  ) {
    super();
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(2, ProfileElement.SCHEMA_VERSION);
  }

  get $typeId(): string {
    return ProfileElement.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): ProfileElement {
    return new ProfileElement(
        MapUtils.get<string>(map, 'uniqueId'),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        MapUtils.get<string>(map, 'name'),
        toEnumFromString(MapUtils.get<string>(map, '$status'), ProfileElementStatus),
        MapUtils.getOrNull<string>(map, 'webRoute'),
        ComponentList.deserialize(context, MapUtils.get<SerializedData[]>(map, 'components')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'businessRef': this.businessRef.encode(),
      'name': this.name,
      '$status': this.status,
      'webRoute': this.webRoute,
      'components': this.components.serialize(context),
    };
  }

  clone(): this {
    return new ProfileElement(
        this.uniqueId,
        this.businessRef.clone(),
        this.name,
        this.status,
        this.webRoute,
        this.components.clone(),
    ) as this;
  }

  isSame(other: ProfileElement): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
          isSameData(other.businessRef, this.businessRef) &&
          other.name === this.name &&
          other.status === this.status &&
          other.webRoute === this.webRoute &&
          isSameData(other.components, this.components);
  }

  copyWithBusinessRef(businessRef: BusinessModuleRef): this {
    return new ProfileElement(
        this.uniqueId,
        businessRef.clone(),
        this.name,
        this.status,
        this.webRoute,
        this.components.copyWithBusinessRef(businessRef),
    ) as this;
  }

  static encodedStatusSafe(status: ProfileElementStatus): string {
    return fromEnumAsString(status);
  }

  hasComponentByTypeId(typeId: string): boolean {
    return this.components.hasComponentByTypeId(typeId);
  }

  mustFindComponentByTypeId<C extends Component>(typeId: string): C {
    return this.components.mustFindComponentByTypeId<C>(typeId);
  }

  findComponentByTypeId<C extends Component>(typeId: string): C | null {
    return this.components.findComponentByTypeId<C>(typeId);
  }

  findComponentByInstanceId<C extends Component>(instanceId: string): C | null {
    return this.components.findComponentByInstanceId<C>(instanceId);
  }

  findComponentsByTypeId<C extends Component>(typeId: string): C[] {
    return this.components.findComponentsByTypeId<C>(typeId);
  }

  addComponent(component: Component): void {
    this.components.addComponent(component);
  }

  addComponents(list: Component[]): void {
    this.components.addComponents(list);
  }

  removeComponentByTypeId(typeId: string): boolean {
    return this.components.removeComponentByTypeId(typeId);
  }

  removeComponentByInstanceId(instanceId: string): boolean {
    return this.components.removeComponentByInstanceId(instanceId);
  }

  removeComponentAt(index: number): void {
    this.components.removeComponentAt(index);
  }

  removeAllComponents(): void {
    this.components.removeAllComponents();
  }

  popAllComponentsByTypeId<C extends Component>(typeId: string): C[] {
    return this.components.popAllComponentsByTypeId(typeId);
  }

  popComponentByTypeId<C extends Component>(typeId: string): C | null {
    return this.components.popComponentByTypeId(typeId);
  }

  popComponent<C extends Component>(component: C): C | null {
    return this.components.popComponent(component);
  }

  replaceComponent<C extends Component>(replacement: C): boolean {
    return this.components.replaceComponent(replacement);
  }

  addOrReplaceComponent<C extends Component>(component: C): void {
    this.components.addOrReplaceComponent(component);
  }

  mustFindComponentField<C extends Component, F>(componentTypeId: string, find: (c: C) => F): F {
    return this.components.mustFindComponentField<C, F>(componentTypeId, find);
  }

  findComponentField<C extends Component, F>(componentTypeId: string, find: (c: C) => F): F | null {
    return this.components.findComponentField<C, F>(componentTypeId, find);
  }
}

export class ProfileElementBuilder extends DataModelBuilder<ProfileElement> {
  build(context: SerializationContext, map: SerializedData): ProfileElement {
    return ProfileElement.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(ProfileElement.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(ProfileElement.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'businessRef', MapUtils.get(map, 'businessId'));
      version = 2;
    }

    // In this version we added the [webRoute] field
    if(version < 3) {
      map['webRoute'] = null;
      version = 3;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
